import { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, Alert } from '@mui/material';
import { getSlipPeriodConfig, saveSlipPeriodConfig } from '../../api/generalSettingsApi';

const PP = "'Poppins', sans-serif";

// Commission-slip period (duration) — up to 3 rounds per month. R3 optional (leave blank
// for a 2-round cycle). The last filled round always runs to end-of-month. Effective-dated:
// a change applies to periods on/after the Effective date; older slips keep their duration.
export default function CommissionCycleTab() {
    const [rows, setRows] = useState([
        { name: 'R1', startDay: 1, endDay: 15 },
        { name: 'R2', startDay: 16, endDay: 31 },
        { name: 'R3', startDay: '', endDay: '' },
    ]);
    const [effectiveFrom, setEffectiveFrom] = useState('');   // yyyy-mm-dd; blank = today
    const [msg, setMsg] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getSlipPeriodConfig().then((d) => {
            const cur = d?.current || [];
            if (cur.length) {
                setRows([0, 1, 2].map((i) => cur[i]
                    ? { name: cur[i].name || `R${i + 1}`, startDay: cur[i].startDay, endDay: cur[i].endDay }
                    : { name: `R${i + 1}`, startDay: '', endDay: '' }));
            }
        }).catch(() => { });
    }, []);

    const update = (i, patch) => setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

    const save = async () => {
        setSaving(true); setMsg(null);
        try {
            const filled = rows.filter((r) => r.startDay !== '' && r.startDay != null);
            const rounds = filled.map((r, idx) => ({
                name: r.name || `R${idx + 1}`,
                startDay: Number(r.startDay),
                // Last filled round → end-of-month (helper clamps 31 to the real last day).
                endDay: idx === filled.length - 1 ? 31 : Number(r.endDay),
            }));
            await saveSlipPeriodConfig({ rounds, effectiveFrom: effectiveFrom || null });
            setMsg({ type: 'success', text: `Saved. New rounds apply to periods on/after ${effectiveFrom || 'today'} — older slips keep their original duration.` });
        } catch (e) {
            setMsg({ type: 'error', text: e?.response?.data?.message || 'Save failed.' });
        } finally { setSaving(false); }
    };

    return (
        <Box sx={{ maxWidth: 660 }}>
            <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>Commission Slip Cycle (duration)</Typography>
            <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#64748B', mb: 2, lineHeight: 1.5 }}>
                Split each month into rounds. The <b>last filled round runs to end-of-month</b> automatically — leave <b>R3 blank</b> for a 2-round cycle (1–15 / 16–end). Bands must be contiguous and cover the whole month.
                <br />Changes are <b>effective-dated</b>: they apply to periods on/after the Effective date; slips already generated under an earlier duration are never re-sliced.
            </Typography>
            {msg && <Alert severity={msg.type} sx={{ mb: 2 }}>{msg.text}</Alert>}

            {rows.map((r, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
                    <Typography sx={{ fontFamily: PP, fontWeight: 700, width: 36 }}>{r.name || `R${i + 1}`}</Typography>
                    <TextField label="Start day" type="number" size="small" value={r.startDay}
                        onChange={(e) => update(i, { startDay: e.target.value })} inputProps={{ min: 1, max: 31 }} sx={{ width: 130 }} />
                    <TextField label="End day (last → month-end)" type="number" size="small" value={r.endDay}
                        onChange={(e) => update(i, { endDay: e.target.value })} inputProps={{ min: 1, max: 31 }} sx={{ width: 210 }} />
                    {i === 2 && (r.startDay !== '' && r.startDay != null) && (
                        <Button size="small" onClick={() => update(2, { name: 'R3', startDay: '', endDay: '' })} sx={{ textTransform: 'none', fontFamily: PP }}>Clear R3</Button>
                    )}
                </Box>
            ))}

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 2 }}>
                <TextField label="Effective from" type="date" size="small" value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 190 }} />
                <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#94A3B8' }}>blank = today</Typography>
            </Box>

            <Button variant="contained" onClick={save} disabled={saving} sx={{ mt: 2, fontFamily: PP, textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}>
                {saving ? 'Saving…' : 'Save Cycle'}
            </Button>
        </Box>
    );
}
